import { useMemo } from 'react'
import { Icon } from '@winnin/winnin_ui.components.icon'

import ReactSelect from 'components/ReactSelect'
import { useDebounce } from 'hooks'
import { useTranslate } from 'lib/tolgee'
import { TOLGEE_GLOBALS } from 'utils'
import './index.scss'

type SelectItem = { value: string; label: string }

type SelectOption = {
  label: string
  options: SelectItem[]
}

interface IChipSelect {
  options: (SelectOption | SelectItem)[]
  onSelect: (value: SelectItem) => void
  selectedValue?: SelectItem
  isDisabled?: boolean
  label: string
  isAsync?: boolean
  onType?: (value: unknown, callback: (...args: any) => void) => void
}

export default function ChipSelect({
  options,
  onSelect,
  selectedValue,
  isDisabled,
  label,
  isAsync,
  onType,
}: IChipSelect) {
  const t = useTranslate()

  const filterStyle = useMemo(() => {
    return {
      noOptionsMessage: base => ({
        ...base,
        color: '#4c5567',
        fontSize: '14px',
      }),
      option: (provided, state) => {
        return {
          ...provided,
          color: '#4c5567',
          fontSize: '14px',
          backgroundColor: state.isFocused ? '#f6f0ff' : 'white',
          cursor: 'pointer',
          '&:hover': {
            color: '#6224C6',
            backgroundColor: '#f6f0ff',
          },
        }
      },
      groupHeading: base => ({
        ...base,
        color: 'var(--color_neutral_500)',
        textTransform: 'capitalize',
        fontSize: '10px',
        margin: '0px',
        padding: '6px 24px 9.5px 14px',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
      }),
      input: base => ({
        ...base,
        color: selectedValue?.value ? 'white' : '#4c5567',
        margin: '0px',
      }),
      control: (base, state) => {
        return {
          ...base,
          border: state.isDisabled ? '1px solid var(--color_neutral_400)' : '1px solid var(--color_primary_500)',
          borderRadius: '20px',
          padding: '4px 8px 4px 16px',
          width: 150,
          justifyContent: 'flex-start',
          gap: 2,
          cursor: 'pointer',
          backgroundColor: selectedValue?.value ? 'var(--color_primary_500)' : '#fff',
          height: '32px',
          minHeight: '32px',

          '&:hover': {
            border: state.isDisabled ? '1px solid var(--color_neutral_400)' : '1px solid var(--color_primary_500)',
          },
        }
      },
      menu: provided => ({
        ...provided,
        width: 196,
        position: 'absolute',
        top: 30,
        right: '0px',
        zIndex: 100,
      }),
      dropdownIndicator: (base, state) => {
        const baseColors = selectedValue?.value ? '#fff' : 'var(--color_primary_500)'
        return {
          ...base,
          padding: 0,
          svg: {
            width: 13,
            height: 13,
            fill: state.isDisabled ? 'var(--color_neutral_400)' : baseColors,
          },
        }
      },
      valueContainer: base => ({
        ...base,
        padding: 0,
        justifyContent: 'flex-start',
        fontSize: '14px',
      }),
      singleValue: (base, state) => {
        const baseColors = selectedValue?.value ? '#fff' : 'var(--color_primary_500)'
        return {
          ...base,
          color: state.isDisabled ? 'var(--color_neutral_400)' : baseColors,
          fontWeight: 400,
          fontSize: '14px',
        }
      },
      indicatorsContainer: base => ({
        ...base,
        margin: 'auto 0',
      }),
    }
  }, [selectedValue])

  const { debouncedAction: debouncedOnType } = useDebounce(onType, 500)

  const handleType = (value, callback) => {
    debouncedOnType(value, callback)
  }

  return (
    <div className="ChipSelect-wrapper">
      <ReactSelect
        components={{
          IndicatorSeparator: () => null,
          DropdownIndicator: () => (
            <Icon
              fill={selectedValue?.value ? 'white' : 'var(--color_primary_500)'}
              className="drop-icon"
              icon="arrowDown"
            />
          ),
        }}
        styles={filterStyle}
        options={options}
        value={selectedValue?.value ? selectedValue : { label: label }}
        onChange={onSelect}
        isDisabled={isDisabled}
        asyncSelect={isAsync}
        {...(isAsync && { defaultOptions: options })}
        loadOptions={handleType}
        noOptionsMessage={({ inputValue }) => {
          return !inputValue ? null : t(TOLGEE_GLOBALS.noResultsFound)
        }}
      />
    </div>
  )
}
